/**
 * EtaWidget — ETA até o porto de descarga.
 *
 * Fase C da integração LAB → principal. LÊ a projeção JÁ computada pelo worker
 * server-side (GET /api/lab/eta/{id}); NÃO recalcula nada (sem haversine, sem
 * nearestIndexOnRoute, sem matemática de tempo) — apenas formata o `arrival_iso`
 * que o servidor já entregou. Respeita INV-LAYER-001 / INV-UI-TRAIL-001.
 *
 * Polling de 15s (4/min, bem abaixo do anti-storm de 30/60s).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Navigation, Clock, Fuel, Route as RouteIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { activityApi } from '@/services/activityApi';
import { useTimezone } from '@/contexts/TimezoneContext';

const POLL_MS = 15000;

const fmtHours = (h) => {
  if (h == null || !Number.isFinite(Number(h))) return '—';
  const totalMin = Math.round(Number(h) * 60);
  const hh = Math.floor(totalMin / 60);
  const mm = totalMin % 60;
  return hh > 0 ? `${hh}h ${mm}min` : `${mm}min`;
};

const fmtKm = (km) =>
  km == null || !Number.isFinite(Number(km))
    ? '—'
    : `${Number(km).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} km`;

const fmtL = (l) =>
  l == null || !Number.isFinite(Number(l))
    ? '—'
    : `${Math.round(Number(l)).toLocaleString('pt-BR')} L`;

const EtaWidget = ({ equipmentId }) => {
  const { formatDateTime } = useTimezone();
  const [eta, setEta] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!equipmentId) return;
    try {
      const res = await activityApi.getEta(equipmentId);
      setEta(res.data || null);
    } catch (e) {
      // 429 do anti-storm ou indisponibilidade transitória — mantém o último valor.
      console.warn('[EtaWidget] erro ao carregar ETA:', e?.response?.status || e);
    } finally {
      setLoaded(true);
    }
  }, [equipmentId]);

  useEffect(() => {
    setLoaded(false);
    setEta(null);
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const available = eta?.available;

  return (
    <Card data-testid="eta-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Navigation className="h-4 w-4 text-primary" />
          ETA até o porto
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!loaded ? (
          <p className="text-sm text-muted-foreground">Carregando…</p>
        ) : !available ? (
          <p className="text-sm text-muted-foreground">
            Sem projeção de chegada no momento
            {eta?.activity ? ` (estado: ${eta.activity}${(eta.cargo_state || eta.subdir) ? ` ${eta.cargo_state || eta.subdir}` : ''})` : ''}.
            A ETA é calculada pelo servidor durante a viagem.
          </p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-2xl font-semibold">{fmtHours(eta.eta_h)}</span>
              {eta.route_id && (
                <Badge variant="outline" className="gap-1">
                  <RouteIcon className="h-3 w-3" />
                  {eta.route_id}
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Chegada prevista:{' '}
              <span className="text-foreground font-medium">
                {eta.arrival_iso ? formatDateTime(eta.arrival_iso) : '—'}
              </span>
            </p>
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div className="flex items-center gap-2 text-sm">
                <Navigation className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Distância:</span>
                <span className="font-medium">{fmtKm(eta.dist_km)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Fuel className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Combustível:</span>
                <span className="font-medium">{fmtL(eta.consumo_l)}</span>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground flex items-center gap-1 pt-1">
              <Clock className="h-3 w-3" />
              Projeção do servidor{eta.updated_at ? ` • atualizado ${formatDateTime(eta.updated_at)}` : ''}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EtaWidget;
